// src/components/portal/SupervisorPortalDashboard.js
import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { getFunctions } from "firebase/functions";
import { httpsCallable } from "../../shared/legacyData/legacyCallable";
import StudentProgressModal from "./StudentProgressModal";
import { fetchAIContentBuilderData, buildUnitId } from "../learningDashboard/components/contentHelpers/contentFetch";
import { collectAllProgrammeUnitIds, fetchProgramLearnerProgressUnitsMap } from "../learningDashboard/components/learningProgressSnapshotUtils";
import "./Portal.css";
import TPC_LOGO from "../../images/TPC-logo-light.png";

const COMPANY          = "TPC_Group";
const COLLECTION_BRAND = "TechPerformanceCentre";
const PASS_RATE        = 70;

const fmtPct  = (v) => `${Math.round(v || 0)}%`;
const normalizeFilterValue = (value) => String(value || "").trim().toLowerCase();
const pctClr  = (v) => v >= 70 ? "var(--p-green)" : v >= 50 ? "var(--p-amber)" : "var(--p-red)";
const pctBg   = (v) => v >= 70 ? "var(--p-green-bg)" : v >= 50 ? "var(--p-amber-bg)" : "var(--p-red-bg)";
const hasCompletedAllUnits = (written, total, progressPercent = 0) => (
Number(progressPercent || 0) >= 100 ||
(Number(total || 0) > 0 && Number(written || 0) === Number(total || 0))
);
const getCNYC = (written, total, avg, pr = 70, progressPercent = 0) => {
if (written <= 0 && Number(progressPercent || 0) < 100) return "NOT_STARTED";
if (!hasCompletedAllUnits(written, total, progressPercent)) return "IN_PROGRESS";
if (avg >= pr) return "COMPLETED";
return "COMPLETED_NC";
};
const cnycLabel = (s) => ({
"COMPLETED":    "Completed",
"COMPLETED_NC": "Not Complete",
"IN_PROGRESS":  "In progress",
"NOT_STARTED":  "Not started",
"C":            "Completed",
"NYC-NS":       "Not started",
"NYC-BP":       "Not Complete",
})[s] || "In progress";
const cnycClass = (s) => ({
"COMPLETED":    "portal-badge--c",
"COMPLETED_NC": "portal-badge--nyc-bp",
"IN_PROGRESS":  "portal-badge--in-progress",
"NOT_STARTED":  "portal-badge--nyc-ns",
"C":            "portal-badge--c",
"NYC-NS":       "portal-badge--nyc-ns",
"NYC-BP":       "portal-badge--nyc-bp",
})[s] || "portal-badge--in-progress";

const normalizeEmail = (value) => String(value || "").trim().toLowerCase();

export default function SupervisorPortalDashboard() {
const navigate = useNavigate();
const [user,        setUser]        = useState(null);
const [supervisor,  setSupervisor]  = useState(null);
const [funder,      setFunder]      = useState(null);
const [students,    setStudents]    = useState([]);
const [compilationStudents, setCompilationStudents] = useState([]);
const [allIntakeBlocks, setAllIntakeBlocks] = useState([]);
const [loading,     setLoading]     = useState(true);
const [progressLoading, setProgressLoading] = useState(false);
const [error,       setError]       = useState("");
const [activeModal, setActiveModal] = useState(null);
const [searchQ,     setSearchQ]     = useState("");
const [studentFilter, setStudentFilter] = useState("all");
const [studentPageSize, setStudentPageSize] = useState("25");
const [studentPage, setStudentPage] = useState(1);
const [reportLoading, setReportLoading] = useState({});
const [supReportLoading, setSupReportLoading] = useState(false);
const [lastUpdatedAt, setLastUpdatedAt] = useState(null);

// Auth guard
useEffect(() => {
const stored = sessionStorage.getItem("portalUser");
if (!stored) { navigate("/supervisor-login", { replace: true }); return; }
try {
    const parsed = JSON.parse(stored);
    if (parsed.role !== "supervisor") { navigate("/funder-dashboard", { replace: true }); return; }
    setUser(parsed);
} catch { navigate("/supervisor-login", { replace: true }); }
}, [navigate]);

const openStudentProgressModal = useCallback((student) => {
const email = normalizeEmail(student?.email);
const enrichedStudent = students.find((item) => normalizeEmail(item?.email) === email);
setActiveModal({ ...(enrichedStudent || {}), ...(student || {}), assessmentStory: enrichedStudent?.assessmentStory || student?.assessmentStory });
}, [students]);

const enrichStudentsInBackground = useCallback(async (assignments, intakeBlocks = []) => {
try {
    const gsd = httpsCallable(getFunctions(), "getSystemDocuments");
    let allStories = [];
    try {
    const storyRes = await gsd({ company: COMPANY, collectionBrand: COLLECTION_BRAND, systemName: "learnerAssessmentStory", auth: true });
    allStories = rowsWithDocumentIds(storyRes?.data?.data);
    } catch (storyErr) {
    console.warn("[SupervisorPortalDashboard] bulk story load failed, using direct lookup:", storyErr?.message);
    }
    const storyMap   = new Map();
    allStories.forEach((s) => {
    if (!s) return;
    [
        s.id, s.email, s.learnerId, s.uid, s.userId, s.docId, s.userEmail, s.learnerEmail,
        s.data?.id, s.data?.email, s.data?.learnerId, s.data?.uid, s.data?.userId, s.data?.docId, s.data?.userEmail, s.data?.learnerEmail,
    ].filter(Boolean)
        .forEach((k) => {
        const key = String(k).toLowerCase().trim();
        if (key && !storyMap.has(key)) storyMap.set(key, s);
        });
    });

    const directRead = httpsCallable(getFunctions(), "firestoreReadDocSafe");
    const assignmentEmails = assignments.map((s) => normalizeEmail(s?.email)).filter(Boolean);
    const unmatchedEmails = assignmentEmails.filter((email) => {
    const directStory = storyMap.get(email);
    return !directStory || resolveHistory(directStory).length === 0;
    });
    if (unmatchedEmails.length) {
    let uidMap = {};
    try {
        const applicantRes = await gsd({ company: COMPANY, collectionBrand: COLLECTION_BRAND, systemName: "Applicants", auth: true });
        const applicants = Array.isArray(applicantRes?.data?.data) ? applicantRes.data.data : Object.values(applicantRes?.data?.data || {});
        const needed = new Set(unmatchedEmails);
        applicants.forEach((app) => {
        const email = normalizeEmail(app?.email);
        if (!needed.has(email)) return;
        const uid = app.uid || app.firebaseUid || app.userId || app.firebase_uid || app.authUid || "";
        if (uid) uidMap[email] = uid;
        });
    } catch (appErr) {
        console.warn("[SupervisorPortalDashboard] applicant UID lookup error:", appErr?.message);
    }

    await Promise.all(unmatchedEmails.map(async (email) => {
        const assignment = assignments.find((s) => normalizeEmail(s?.email) === email) || {};
        const docIds = [
        uidMap[email],
        assignment.uid,
        assignment.firebaseUid,
        assignment.userId,
        assignment.authUid,
        assignment.learnerId,
        assignment.id,
        email,
        email.replace("@", "_at_").replace(/\./g, "_"),
        ].filter(Boolean);
        for (const docId of docIds) {
        try {
            const res = await directRead({ company: COMPANY, collectionBrand: COLLECTION_BRAND, systemName: "learnerAssessmentStory", uniqueId: docId, auth: true });
            const story = res?.data?.found && res?.data?.data ? res.data.data : null;
            if (story && resolveHistory(story).length > 0) {
            storyMap.set(email, story);
            [story.id, story.email, story.learnerId, story.uid, story.userId, story.docId, story.userEmail, story.learnerEmail]
                .filter(Boolean).forEach((key) => storyMap.set(String(key).toLowerCase().trim(), story));
            return;
            }
        } catch { /* try next id */ }
        }
    }));
    }

    const programmeIds = Array.from(new Set(
    assignments.map((stu) => resolveStudentProgrammeId(stu, intakeBlocks)).filter(Boolean)
    ));
    const programmeMeta = new Map();
    await Promise.all(programmeIds.map(async (programmeId) => {
    const meta = await resolveProgrammeProgressMeta(programmeId);
    programmeMeta.set(programmeId, meta);
    }));

    return await Promise.all(assignments.map(async (stu) => {
    const lookupKeys = [stu.email, stu.id, stu.uid, stu.userId, stu.learnerId, stu.authUid, stu.firebaseUid]
        .map((v) => String(v || "").toLowerCase().trim())
        .filter(Boolean);
    const story = lookupKeys.map((key) => storyMap.get(key)).find(Boolean) || null;
    const programmeId = resolveStudentProgrammeId(stu, intakeBlocks);
    const meta        = programmeMeta.get(programmeId) || {};
    const programmeTotal = meta.assessmentTotal || 0;
    const unitProgress   = await resolveLearnerUnitProgress(programmeId, stu, meta.unitIds || []);

    if (!story) {
        return {
        ...stu,
        totalAssessments:      programmeTotal,
        totalAssessmentsKnown: programmeTotal > 0,
        progressPercent:       unitProgress.percent,
        unitProgressDone:      unitProgress.done,
        unitProgressTotal:     unitProgress.total,
        unitProgressPercent:   unitProgress.percent,
        cnycStatus: getCNYC(0, programmeTotal, 0, PASS_RATE, unitProgress.percent),
        };
    }

    const history = resolveHistory(story);
    const submittedByAssessment = new Map();
    history.forEach((entry) => {
        if (!entry?.assessment) return;
        const key = normalizeAssessmentKey(entry) || `assessment-${submittedByAssessment.size}`;
        const percent = pctFromEntry(entry);
        submittedByAssessment.set(key, Math.max(submittedByAssessment.get(key) ?? 0, percent));
    });
    const submittedScores = Array.from(submittedByAssessment.values());
    const explicitTotal = resolveTotalAssessments(story);
    const explicitCompleted = resolveCompletedAssessments(story);
    const storyProgressPercent = resolveProgressPercent(story);
    const progressPercent = unitProgress.total > 0 ? unitProgress.percent : storyProgressPercent;
    const totalAssessments = explicitTotal || programmeTotal || submittedScores.length;
    const writtenFromStory = explicitCompleted > 0 && totalAssessments > 0
        ? Math.min(explicitCompleted, totalAssessments) : 0;
    const written = writtenFromStory || (totalAssessments > 0
        ? Math.min(submittedScores.length, totalAssessments) : submittedScores.length);
    const rawSum = submittedScores.slice(0, written).reduce((sum, score) => sum + score, 0);
    const storedAvg = resolveCurrentAverage(story);
    const currentAvg = storedAvg || (written > 0
        ? Math.round(rawSum / Math.max(1, Math.min(written, submittedScores.length || written))) : 0);
    const cnycStatus = getCNYC(written, totalAssessments, currentAvg, PASS_RATE, progressPercent);
    return {
        ...stu,
        assessmentStory: story,
        written,
        totalAssessments,
        totalAssessmentsKnown: totalAssessments > 0,
        progressPercent,
        unitProgressDone:    unitProgress.done,
        unitProgressTotal:   unitProgress.total,
        unitProgressPercent: unitProgress.percent,
        currentAvg,
        cnycStatus,
    };
    }));
} catch (err) {
    console.warn("[SupervisorPortalDashboard] story enrichment error:", err?.message);
    return assignments;
}
}, []);

const loadAll = useCallback(async (portalUser) => {
setLoading(true);
setProgressLoading(false);
setError("");
try {
    const gsd = httpsCallable(getFunctions(), "getSystemDocuments");

    // ── Fetch supervisor record ──────────────────────────────────────────
    const supRes  = await gsd({ company: COMPANY, collectionBrand: COLLECTION_BRAND, systemName: "supervisors", auth: true });
    const allSups = Array.isArray(supRes?.data?.data) ? supRes.data.data : Object.values(supRes?.data?.data || {});
    const supRecord = allSups.find((s) => (s.email || "").toLowerCase() === portalUser.email);
    if (!supRecord) throw new Error("Supervisor record not found.");
    setSupervisor(supRecord);

    // ── Fetch funder record ──────────────────────────────────────────────
    const fundersRes = await gsd({ company: COMPANY, collectionBrand: COLLECTION_BRAND, systemName: "funders", auth: true });
    const allFunders = Array.isArray(fundersRes?.data?.data) ? fundersRes.data.data : Object.values(fundersRes?.data?.data || {});
    const funderRecord = allFunders.find((f) => f.id === supRecord.funderId);
    setFunder(funderRecord || null);

    let normalizedIntakes = [];
    try {
    const intakeRes  = await gsd({ company: COMPANY, collectionBrand: COLLECTION_BRAND, systemName: "intakeBlocks", auth: true });
    const intakeRows = rowsWithDocumentIds(intakeRes?.data?.data);
    normalizedIntakes = intakeRows.map((b, idx) => ({
        ...b,
        id:          String(b?.id || b?.uid || b?.uniqueId || b?.intakeId || b?.docId || ""),
        label:       String(b?.label || b?.name || b?.intakeName || b?.id || b?.uid || `Intake ${idx + 1}`),
        programmeId: String(b?.programme || b?.program || b?.programmeId || b?.programId || "").trim(),
    })).filter((b) => b.id);
    setAllIntakeBlocks(normalizedIntakes);
    } catch (intakeErr) {
    console.warn("[SupervisorPortalDashboard] intake blocks load error:", intakeErr?.message);
    setAllIntakeBlocks([]);
    }

    // ── Filter students to only this supervisor's assigned learners ───────
    const learnerEmails = new Set((supRecord.learners || []).map((e) => String(e || "").toLowerCase()));
    const allAssignments = Array.isArray(funderRecord?.studentAssignments) ? funderRecord.studentAssignments : [];
    const myAssignments  = allAssignments.filter((s) => learnerEmails.has((s.email || "").toLowerCase()));
    const applicantEnrollmentByEmail = new Map();
    const applicantProgrammesByEmail = new Map();
    try {
    if (learnerEmails.size) {
        const applicantRes = await gsd({ company: COMPANY, collectionBrand: COLLECTION_BRAND, systemName: "Applicants", auth: true });
        rowsWithDocumentIds(applicantRes?.data?.data).forEach((app) => {
        const email = normalizeEmail(app?.email || app?.id || app?.docId);
        if (!learnerEmails.has(email)) return;
        const selectedIds = Array.isArray(app?.selectedIntakeBlockIds)
            ? app.selectedIntakeBlockIds
            : app?.selectedIntakeBlockId ? [app.selectedIntakeBlockId] : [];
        applicantEnrollmentByEmail.set(email, selectedIds.map((id) => String(id || "").trim()).filter(Boolean));
        applicantProgrammesByEmail.set(email, collectStudentProgrammeValues(app));
        });
    }
    } catch (applicantErr) {
    console.warn("[SupervisorPortalDashboard] applicant enrollment lookup failed:", applicantErr?.message);
    }

    const baseStudents = myAssignments.flatMap((s) => {
    const applicantIntakeIds = applicantEnrollmentByEmail.get(normalizeEmail(s?.email)) || [];
    const applicantProgrammes = applicantProgrammesByEmail.get(normalizeEmail(s?.email)) || [];
    const intakeAssignment = { ...(s.intakeAssignment && typeof s.intakeAssignment === "object" ? s.intakeAssignment : {}) };
    applicantIntakeIds.forEach((id) => { intakeAssignment[id] = true; });
    const selectedIntakeBlockIds = Array.from(new Set([
        ...(Array.isArray(s.selectedIntakeBlockIds) ? s.selectedIntakeBlockIds : []),
        ...applicantIntakeIds,
    ].map((id) => String(id || "").trim()).filter(Boolean)));
    const enrollmentSource = {
        ...s,
        intakeAssignment,
        selectedIntakeBlockIds,
        enrolledProgrammes: Array.from(new Set([...(Array.isArray(s.enrolledProgrammes) ? s.enrolledProgrammes : []), ...applicantProgrammes])),
        selectedIntakeBlockId: s.selectedIntakeBlockId || selectedIntakeBlockIds[0] || "",
    };
    const assignedIntakes = resolveAssignedStudentIntakes(enrollmentSource, normalizedIntakes);
    const rows            = assignedIntakes.length ? assignedIntakes : [null];
    return rows.map((assignedIntake, index) => {
    const assignedIntakeId = assignedIntake ? String(assignedIntake.id || assignedIntake.uid || assignedIntake.label || "").trim() : "";
    return {
    id:          s.id || s.uid || s.userId || s.learnerId || "",
    rowId:       `${(s.email || s.id || s.uid || index || "").toString().toLowerCase()}__${assignedIntakeId || `programme_${index}`}`,
    uid:         s.uid || s.firebaseUid || s.userId || s.authUid || "",
    userId:      s.userId || "",
    learnerId:   s.learnerId || "",
    authUid:     s.authUid || "",
    firebaseUid: s.firebaseUid || "",
    email:       (s.email || "").toLowerCase(),
    fullName:    s.fullName || s.name || s.email || "—",
    programmeId: assignedIntake?.programmeId || s.programmeId || "",
    intakeLabel: s.intakeBlock?.label || assignedIntake?.label || "",
    intakeAssignment: assignedIntakeId ? { [assignedIntakeId]: true } : intakeAssignment,
    selectedIntakeBlockIds: assignedIntakeId ? [assignedIntakeId] : selectedIntakeBlockIds,
    selectedIntakeBlockId: assignedIntakeId || enrollmentSource.selectedIntakeBlockId || "",
    written:     0,
    totalAssessments: 0,
    totalAssessmentsKnown: false,
    progressPercent:       0,
    unitProgressDone:      0,
    unitProgressTotal:     0,
    unitProgressPercent:   0,
    currentAvg:  0,
    cnycStatus:  "NOT_STARTED",
    };
    });
    });

    setStudents(baseStudents);
    setCompilationStudents([]);
    setLoading(false);
    setProgressLoading(baseStudents.length > 0);

    const helperPromise = import("../heroManagementDashboard/adminDashboard/FunderProgressReportHelper");
    const enrichedStudents = await enrichStudentsInBackground(baseStudents, normalizedIntakes);
    setStudents(enrichedStudents);
    setCompilationStudents([]);

    try {
    const { buildFunderCompilationStudentData } = await helperPromise;
    const reportStudents = await buildFunderCompilationStudentData({
        funder: { ...(funderRecord || {}), studentAssignments: enrichedStudents },
        allIntakeBlocks: normalizedIntakes,
    });
    const allowedEmails = new Set(enrichedStudents.map((stu) => normalizeEmail(stu.email)));
    setCompilationStudents(reportStudents.filter((stu) => allowedEmails.has(normalizeEmail(stu.email))));
    } catch (summaryErr) {
    console.warn("[SupervisorPortalDashboard] compilation summary load failed:", summaryErr?.message);
    }
    setLastUpdatedAt(new Date());
    setProgressLoading(false);

} catch (err) {
    console.error("[SupervisorPortalDashboard] loadAll error:", err);
    setError(err.message || "Failed to load your dashboard. Please try again.");
} finally {
    setLoading(false);
    setProgressLoading(false);
}
}, [enrichStudentsInBackground]);

useEffect(() => { if (user) loadAll(user); }, [user, loadAll]);
useEffect(() => { setStudentPage(1); }, [searchQ, studentFilter, studentPageSize]);

function handleSignOut() {
sessionStorage.removeItem("portalUser");
navigate("/supervisor-login", { replace: true });
}

async function handleDownloadReport(student) {
const loadingKey = student.rowId || student.email;
setReportLoading((prev) => ({ ...prev, [loadingKey]: true }));
try {
    const { downloadFunderStudentReport } = await import("../heroManagementDashboard/adminDashboard/FunderProgressReportHelper");
    await downloadFunderStudentReport({ student, funder, allIntakeBlocks });
} catch (err) {
    alert("Could not generate report: " + (err?.message || "Please try again."));
} finally {
    setReportLoading((prev) => ({ ...prev, [loadingKey]: false }));
}
}

async function handleDownloadSupReport() {
if (!supervisor || !funder) return;
setSupReportLoading(true);
try {
    const { downloadSupervisorReport } = await import("../heroManagementDashboard/adminDashboard/FunderProgressReportHelper");
    await downloadSupervisorReport({ supervisor, funder: { ...(funder || {}), studentAssignments: students }, allIntakeBlocks });
} catch (err) {
    alert("Could not generate report: " + (err?.message || "Please try again."));
} finally {
    setSupReportLoading(false);
}
}

// ── Computed stats ─────────────────────────────────────────────────────────
const total = students.length;
const completionRows = compilationStudents.length ? compilationStudents : students;
const progressByEmail = useMemo(() => {
const map = new Map();
(compilationStudents || []).forEach((stu) => {
    if (stu.rowId) map.set(stu.rowId, stu);
    map.set(normalizeEmail(stu.email), stu);
});
return map;
}, [compilationStudents]);

const getDisplayProgress = useCallback((student) => {
const report = progressByEmail.get(student?.rowId) || progressByEmail.get(normalizeEmail(student?.email));
const totalAssessments = Number(report?.overallTotal ?? student?.totalAssessments ?? 0);
const written = Math.min(Number(report?.overallWritten ?? student?.written ?? 0), totalAssessments || Number(report?.overallWritten ?? student?.written ?? 0));
const submittedWritten = Math.min(Number(report?.overallSubmitted ?? report?.submittedWritten ?? student?.submittedWritten ?? student?.written ?? 0), totalAssessments || Number(report?.overallSubmitted ?? report?.submittedWritten ?? student?.submittedWritten ?? student?.written ?? 0));
const currentAvg = Number(report?.currentAvg ?? student?.currentAvg ?? 0);
const passRate = Number(report?.agg?.passRate || PASS_RATE);
const cnycStatus = totalAssessments > 0
    ? (submittedWritten >= totalAssessments && written < totalAssessments ? "NYC-BP" : getCNYC(written, totalAssessments, currentAvg, passRate, student?.progressPercent || 0))
    : (student?.cnycStatus || "NOT_STARTED");
return { totalAssessments, written, submittedWritten, currentAvg, cnycStatus, progressText: totalAssessments > 0 ? `${written}/${totalAssessments}` : "Not started" };
}, [progressByEmail]);

const started    = students.filter((s) => getDisplayProgress(s).written > 0).length;
const notStarted = students.filter((s) => getDisplayProgress(s).written === 0).length;
const complete   = completionRows.filter((s) => {
const totalAssessments = Number(s.overallTotal ?? s.totalAssessments ?? 0);
const completedAssessments = Number(s.overallWritten ?? s.written ?? 0);
const passRate = Number(s.agg?.passRate || PASS_RATE);
return totalAssessments > 0 && completedAssessments >= totalAssessments && Number(s.currentAvg || 0) >= passRate;
}).length;
const belowPass  = students.filter((s) => ["COMPLETED_NC", "NYC-BP"].includes(getDisplayProgress(s).cnycStatus)).length;
const avgStarted = started > 0
? Math.round(students.filter((s) => getDisplayProgress(s).written > 0).reduce((a, s) => a + getDisplayProgress(s).currentAvg, 0) / started)
: 0;
const completionRate = total > 0 ? Math.round((complete / total) * 100) : 0;
const updateLabel = formatRelativeTime(lastUpdatedAt);

const supName   = supervisor ? `${supervisor.name || ""} ${supervisor.surname || ""}`.trim() : user?.name || "";
const funderDisplayName = funder?.FunderName || funder?.funderName || supervisor?.funderName || "—";

function getStudentIntakeIds(student) {
const fromAssignment = Object.entries(student.intakeAssignment || {}).filter(([, enabled]) => Boolean(enabled)).map(([id]) => id);
const fromSelected = Array.isArray(student.selectedIntakeBlockIds) ? student.selectedIntakeBlockIds : [];
const fromSingle = student.selectedIntakeBlockId ? [student.selectedIntakeBlockId] : [];
return Array.from(new Set([...fromAssignment, ...fromSelected, ...fromSingle].map((id) => String(id || "").trim()).filter(Boolean)));
}

function getIntakeLabel(intakeId) {
const info = allIntakeBlocks.find((b) => b.id === intakeId || b.uid === intakeId || b.label === intakeId || b.name === intakeId);
return info?.label || info?.name || intakeId;
}

const programmeOptions = Array.from(new Map(students
.map((s) => s.intakeLabel || s.programmeId)
.filter(Boolean)
.map((p) => [normalizeFilterValue(p), p])).values())
.sort((a, b) => a.localeCompare(b));

const filterStudents = (rows) => rows
.filter((s) => {
    const q = normalizeFilterValue(searchQ);
    if (!q) return true;
    const intakeLabels = getStudentIntakeIds(s).map(getIntakeLabel).join(" ");
    return [s.fullName, s.email, s.intakeLabel, s.programmeId, intakeLabels]
    .some((value) => normalizeFilterValue(value).includes(q));
})
.filter((s) => {
    const progress = getDisplayProgress(s);
    if (studentFilter === "started") return progress.written > 0;
    if (studentFilter === "not-started") return progress.written === 0;
    if (studentFilter.startsWith("programme:")) {
    const selected = normalizeFilterValue(studentFilter.slice("programme:".length));
    return normalizeFilterValue(s.intakeLabel || s.programmeId) === selected;
    }
    return true;
});

const filtered = filterStudents(students).sort((a, b) => {
if (studentFilter !== "avg-low-high") return 0;
const aP = getDisplayProgress(a);
const bP = getDisplayProgress(b);
const diff = (aP.written > 0 ? aP.currentAvg : 0) - (bP.written > 0 ? bP.currentAvg : 0);
return diff || (a.fullName || "").localeCompare(b.fullName || "");
});
const studentPageLimit = studentPageSize === "all" ? filtered.length || 1 : Number(studentPageSize);
const studentTotalPages = studentPageSize === "all" ? 1 : Math.max(1, Math.ceil(filtered.length / studentPageLimit));
const currentStudentPage = Math.min(studentPage, studentTotalPages);
const studentPageStartIndex = studentPageSize === "all" ? 0 : (currentStudentPage - 1) * studentPageLimit;
const pagedStudents = studentPageSize === "all" ? filtered : filtered.slice(studentPageStartIndex, studentPageStartIndex + studentPageLimit);
const studentPageFirst = filtered.length ? studentPageStartIndex + 1 : 0;
const studentPageLast = filtered.length ? studentPageStartIndex + pagedStudents.length : 0;

if (loading) return (
<div className="portal-page">
    <PortalTopbar name={supName} onSignOut={handleSignOut} />
    <div className="portal-content">
    <div className="portal-loading"><div className="portal-loading__spinner" />Loading your dashboard…</div>
    </div>
</div>
);

if (error) return (
<div className="portal-page">
    <PortalTopbar name={supName} onSignOut={handleSignOut} />
    <div className="portal-content">
    <div style={{ background: "var(--p-red-bg)", color: "var(--p-red)", borderRadius: 12, padding: "20px 24px" }}>{error}</div>
    </div>
</div>
);

return (
<div className="portal-page">
    <PortalTopbar name={supName} role="Supervisor Portal" onSignOut={handleSignOut} />

    <div className="portal-content">

    {/* ── Supervisor Profile Card ──────────────────────────────────────── */}
    <div className="portal-hero-card">
        <div className="portal-hero-card__main">
        <div className="portal-hero-card__logo-placeholder">{supName[0] || "S"}</div>
        <div className="portal-hero-card__copy">
            <div className="portal-hero-card__badge">
            <span className="portal-hero-card__badge-dot" />
            Supervisor
            </div>
            <h1 className="portal-hero-card__name">{supName || "Supervisor"}</h1>
            <p className="portal-hero-card__tagline">Managing learner progress for {funderDisplayName}</p>
            <div className="portal-hero-card__details">
            {[
                ["Position", supervisor?.position || "Supervisor"],
                ["Email", supervisor?.email],
                ["Funder", funderDisplayName],
            ].filter(([, value]) => value).map(([label, value]) => (
                <div key={label} className={`portal-hero-card__detail ${label === "Email" ? "portal-hero-card__detail--wide" : ""}`}>
                <span>{label}</span>
                <strong>{value}</strong>
                </div>
            ))}
            </div>
        </div>
        </div>

        <div className="portal-hero-card__right">
        <div className="portal-hero-card__actions">
            <button type="button" className="portal-hero-pill portal-hero-pill--live" onClick={() => user && loadAll(user)} disabled={!user || loading || progressLoading} title="Refresh dashboard data">
            <span />
            {progressLoading ? "Refreshing" : "Live Data"}
            </button>
            <div className="portal-hero-pill" title={lastUpdatedAt ? lastUpdatedAt.toLocaleString("en-ZA") : "Dashboard has not refreshed yet"}>
            <span />
            {updateLabel}
            </div>
            <button type="button" className="portal-hero-pill" disabled={supReportLoading || !supervisor || !funder} onClick={handleDownloadSupReport}>
            <span />
            {supReportLoading ? "Generating" : "Download My Report"}
            </button>
        </div>
        <div className="portal-hero-metrics" aria-label="Supervisor dashboard summary">
            {[
            ["Assigned Learners", total, "Under supervision"],
            ["Started", started, "Active learners"],
            ["Active Avg", started ? fmtPct(avgStarted) : "-", "Active learners"],
            ["Completion Rate", fmtPct(completionRate), "Completed"],
            ].map(([label, value, sub]) => (
            <div key={label} className="portal-hero-metric">
                <span>{label}</span>
                <strong>{value}</strong>
                <small>{sub}</small>
            </div>
            ))}
        </div>
        </div>
    </div>
    {/* ── Student Stats Section ────────────────────────────────────────── */}
    <div className="portal-section">
        <h2 className="portal-section__heading">My Learner Progress Overview</h2>
        {progressLoading && <div style={{ fontSize: 12, color: "var(--p-grey)", margin: "-4px 0 12px" }}>Refreshing learner progress...</div>}
        <div className="portal-stat-row">
        <StatCard label="Assigned Learners" value={total}       sub="under your supervision"    borderColor="var(--p-navy)" />
        <StatCard label="Started"            value={started}    sub="at least 1 assessment"     borderColor="#1D4ED8"       bgColor="#EFF6FF" />
        <StatCard label="Not Started"        value={notStarted} sub="no activity yet"           borderColor={notStarted > 0 ? "var(--p-red)"   : "var(--p-green)"} bgColor={notStarted > 0 ? "var(--p-red-bg)"   : "var(--p-green-bg)"} />
        <StatCard label="Complete"           value={complete}   sub="all units completed and competent" borderColor="var(--p-green)" bgColor="var(--p-green-bg)" />
        <StatCard label="Below Pass Rate"    value={belowPass}  sub={`< ${PASS_RATE}%`}         borderColor={belowPass > 0 ? "var(--p-red)"    : "var(--p-green)"} bgColor={belowPass > 0 ? "var(--p-red-bg)"    : "var(--p-green-bg)"} />
        <StatCard label="Average of students who started their unity assessments" value={fmtPct(avgStarted)} sub="of active learners" borderColor={pctClr(avgStarted)} bgColor={pctBg(avgStarted)} />
        </div>
    </div>

    {/* My Learners Table */}
    <div className="portal-section portal-section--students">
        <h2 className="portal-section__heading">
        My Learners ({total})
        <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <select value={studentFilter} onChange={(e) => setStudentFilter(e.target.value)} style={{ fontSize: 13, padding: "6px 10px", border: "1.5px solid var(--p-border)", borderRadius: 7, outline: "none", fontWeight: 400, textTransform: "none", minWidth: 170 }}>
            <option value="all">All learners</option>
            <option value="avg-low-high">Avg: low to high</option>
            <option value="started">Started</option>
            <option value="not-started">Not started</option>
            {programmeOptions.map((p) => (
                <option key={p} value={`programme:${p}`}>{p}</option>
            ))}
            </select>
            <input
            type="search"
            placeholder="Search learners..."
            value={searchQ}
            onChange={(e) => setSearchQ(e.target.value)}
            style={{ fontSize: 13, padding: "6px 12px", border: "1.5px solid var(--p-border)", borderRadius: 7, outline: "none", fontWeight: 400, textTransform: "none" }}
            />
        </span>
        </h2>
        {filtered.length === 0 ? (
        <div className="portal-empty">No learners found.</div>
        ) : (
        <>
        <div className="portal-table-wrap">
            <table className="portal-table">
            <thead>
                <tr>
                <th>#</th>
                <th>Name</th>
                <th>Email</th>
                <th>Programme</th>
                <th style={{ textAlign: "center" }}>Progress</th>
                <th style={{ textAlign: "center" }}>Avg</th>
                <th style={{ textAlign: "center" }}>C / NYC</th>
                <th style={{ textAlign: "center" }}>Report</th>
                </tr>
            </thead>
            <tbody>
                {pagedStudents.map((stu, i) => {
                const displayProgress = getDisplayProgress(stu);
                return (
                <tr key={stu.rowId || `${stu.email}-${stu.programmeId || stu.intakeLabel || i}`}>
                    <td style={{ color: "var(--p-grey)", fontSize: 12 }}>{studentPageStartIndex + i + 1}</td>
                    <td>
                    <button className="portal-table__name-btn" onClick={() => openStudentProgressModal(stu)} title="View progress">
                        {stu.fullName}
                    </button>
                    </td>
                    <td style={{ fontSize: 12, color: "var(--p-grey)" }}>{stu.email}</td>
                    <td style={{ fontSize: 12 }}>{stu.intakeLabel || stu.programmeId || "-"}</td>
                    <td style={{ textAlign: "center", fontSize: 12 }}>
                    {displayProgress.totalAssessments > 0 ? (
                        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", lineHeight: 1.25 }}>
                        <strong style={{ color: pctClr(displayProgress.currentAvg), fontSize: 13 }}>{displayProgress.progressText}</strong>
                        <span style={{ color: "var(--p-grey)", fontSize: 11 }}>Assessment Progress</span>
                        </div>
                    ) : <span style={{ color: "var(--p-grey)", fontStyle: "italic" }}>Not started</span>}
                    </td>
                    <td style={{ textAlign: "center", fontWeight: 700, color: pctClr(displayProgress.currentAvg) }}>{displayProgress.written > 0 ? fmtPct(displayProgress.currentAvg) : "-"}</td>
                    <td style={{ textAlign: "center" }}>
                    <span className={`portal-badge ${cnycClass(displayProgress.cnycStatus)}`}>{cnycLabel(displayProgress.cnycStatus)}</span>
                    </td>
                    <td style={{ textAlign: "center" }}>
                    <button className="portal-btn portal-btn--outline" style={{ fontSize: 11 }} disabled={!!reportLoading[stu.rowId || stu.email]} onClick={() => handleDownloadReport(stu)}>
                        {reportLoading[stu.rowId || stu.email] ? "..." : "Report"}
                    </button>
                    </td>
                </tr>
                );
                })}
            </tbody>
            </table>
        </div>
        <div className="portal-table-pagination">
            <div className="portal-table-pagination__summary">
            Showing {studentPageFirst}-{studentPageLast} of {filtered.length} learner(s)
            </div>
            <div className="portal-table-pagination__controls">
            <label className="portal-table-pagination__label">
                Rows
                <select value={studentPageSize} onChange={(e) => setStudentPageSize(e.target.value)} className="portal-table-pagination__select">
                <option value="25">25</option>
                <option value="50">50</option>
                <option value="100">100</option>
                <option value="all">All</option>
                </select>
            </label>
            <button type="button" className="portal-btn portal-btn--outline" onClick={() => setStudentPage((p) => Math.max(1, p - 1))} disabled={currentStudentPage <= 1 || studentPageSize === "all"}>
                Previous
            </button>
            <span className="portal-table-pagination__page">Page {currentStudentPage} of {studentTotalPages}</span>
            <button type="button" className="portal-btn portal-btn--outline" onClick={() => setStudentPage((p) => Math.min(studentTotalPages, p + 1))} disabled={currentStudentPage >= studentTotalPages || studentPageSize === "all"}>
                Next
            </button>
            </div>
        </div>
        </>
        )}
    </div>
    </div>
    {activeModal && (
    <StudentProgressModal student={activeModal} onClose={() => setActiveModal(null)} />
    )}
</div>
);
}

function PortalTopbar({ name, role = "Supervisor Portal", onSignOut }) {
return (
<div className="portal-topbar">
    <img src={TPC_LOGO} alt="TPC" className="portal-topbar__logo" onError={(e) => { e.target.style.display = "none"; }} />
    <div className="portal-topbar__right">
    <span className="portal-topbar__name">{role} · <strong>{name}</strong></span>
    <button className="portal-topbar__signout" onClick={onSignOut}>Sign Out</button>
    </div>
</div>
);
}

function StatCard({ label, value, sub, borderColor, bgColor }) {
return (
<div className="portal-stat-card" style={{ borderLeftColor: borderColor || "var(--p-navy)", background: bgColor || "var(--p-lgrey)" }}>
    <div className="portal-stat-card__label">{label}</div>
    <div className="portal-stat-card__value" style={{ color: borderColor || "var(--p-navy)" }}>{value}</div>
    {sub && <div className="portal-stat-card__sub">{sub}</div>}
</div>
);
}

function resolveHistory(story) {
if (!story) return [];
const candidates = [story.complexityHistory, story.data?.complexityHistory, story.assessmentHistory, story.history, story.grades];
for (const c of candidates) {
if (Array.isArray(c) && c.length > 0 && c[0]?.assessment != null) return c;
}
for (const key of Object.keys(story)) {
const val = story[key];
if (Array.isArray(val) && val.length > 0 && val[0]?.assessment != null) return val;
if (val && typeof val === "object" && !Array.isArray(val)) {
    for (const innerKey of Object.keys(val)) {
    const inner = val[innerKey];
    if (Array.isArray(inner) && inner.length > 0 && inner[0]?.assessment != null) return inner;
    }
}
}
return [];
}

function resolveTotalAssessments(story) {
const candidates = [
story?.overallTotal,
story?.totalAssessments,
story?.assessmentTotal,
story?.totalAssessmentCount,
story?.progress?.overallTotal,
story?.progress?.totalAssessments,
story?.summary?.overallTotal,
story?.summary?.totalAssessments,
story?.data?.overallTotal,
story?.data?.totalAssessments,
story?.data?.assessmentTotal,
story?.data?.totalAssessmentCount,
story?.data?.progress?.overallTotal,
story?.data?.progress?.totalAssessments,
story?.data?.summary?.overallTotal,
story?.data?.summary?.totalAssessments,
];
const found = candidates.map((v) => Number(v)).find((v) => Number.isFinite(v) && v > 0);
return found || 0;
}

function resolveCompletedAssessments(story) {
const candidates = [
story?.overallWritten, story?.completedAssessments, story?.assessmentsCompleted, story?.completedAssessmentCount,
story?.progress?.overallWritten, story?.progress?.completedAssessments, story?.progress?.assessmentsCompleted,
story?.summary?.overallWritten, story?.summary?.completedAssessments, story?.summary?.assessmentsCompleted,
story?.data?.overallWritten, story?.data?.completedAssessments, story?.data?.assessmentsCompleted, story?.data?.completedAssessmentCount,
story?.data?.progress?.overallWritten, story?.data?.progress?.completedAssessments, story?.data?.progress?.assessmentsCompleted,
story?.data?.summary?.overallWritten, story?.data?.summary?.completedAssessments, story?.data?.summary?.assessmentsCompleted,
];
return candidates.map(Number).find((v) => Number.isFinite(v) && v > 0) || 0;
}

function resolveProgressPercent(story) {
const candidates = [
story?.progressPercent, story?.overallPercent, story?.percent,
story?.progress?.percent, story?.progress?.progressPercent,
story?.summary?.percent, story?.summary?.progressPercent,
story?.data?.progressPercent, story?.data?.overallPercent, story?.data?.percent,
story?.data?.progress?.percent, story?.data?.progress?.progressPercent,
story?.data?.summary?.percent, story?.data?.summary?.progressPercent,
];
return candidates.map(Number).find((v) => Number.isFinite(v) && v >= 0) || 0;
}

function resolveCurrentAverage(story) {
const candidates = [
story?.currentAvg, story?.currentAverage, story?.averagePercent, story?.combinedAveragePercent, story?.courseAveragePercent,
story?.progress?.currentAvg, story?.progress?.currentAverage, story?.progress?.averagePercent,
story?.summary?.currentAvg, story?.summary?.currentAverage, story?.summary?.averagePercent,
story?.data?.currentAvg, story?.data?.currentAverage, story?.data?.averagePercent, story?.data?.combinedAveragePercent, story?.data?.courseAveragePercent,
story?.data?.progress?.currentAvg, story?.data?.progress?.currentAverage, story?.data?.progress?.averagePercent,
story?.data?.summary?.currentAvg, story?.data?.summary?.currentAverage, story?.data?.summary?.averagePercent,
];
const avg = candidates.map(Number).find((v) => Number.isFinite(v) && v > 0);
return avg ? Math.round(avg) : 0;
}

function normalizeAssessmentKey(entry) {
return [entry?.sectionTitle, entry?.outcomeName, entry?.assessment?.title, entry?.assessment?.name]
.map((v) => String(v || "").trim().toLowerCase().replace(/\s+/g, " ")).find(Boolean) || "";
}

function rowsWithDocumentIds(data) {
if (Array.isArray(data)) return data;
if (!data || typeof data !== "object") return [];
return Object.entries(data).map(([docId, value]) => {
if (!value || typeof value !== "object" || Array.isArray(value)) return value;
return { ...value, id: value.id || value.uniqueId || docId, uniqueId: value.uniqueId || value.id || docId, docId: value.docId || docId };
});
}

function resolveAssignedStudentIntakes(student, intakeBlocks = []) {
const intakeIds = [
...Object.entries(student?.intakeAssignment || {}).filter(([, enabled]) => Boolean(enabled)).map(([id]) => id),
...(Array.isArray(student?.selectedIntakeBlockIds) ? student.selectedIntakeBlockIds : []),
student?.selectedIntakeBlockId,
].map((id) => String(id || "").trim()).filter(Boolean);
const seen = new Set();
const matches = intakeIds.reduce((items, intakeId) => {
const lowered = intakeId.toLowerCase();
const block = (intakeBlocks || []).find((item) =>
    [item?.id, item?.uid, item?.uniqueId, item?.intakeId, item?.docId, item?.label, item?.name, item?.intakeName, item?.programmeId, item?.programme, item?.program, item?.programId]
    .map((v) => String(v || "").trim().toLowerCase()).includes(lowered));
if (!block) return items;
const programmeId = String(block?.programmeId || block?.programme || block?.program || block?.programId || "").trim();
const key = (programmeId || lowered).toLowerCase();
if (seen.has(key)) return items;
seen.add(key);
items.push(block);
return items;
}, []);
collectStudentProgrammeValues(student).forEach((programmeId) => {
const lowered = programmeId.toLowerCase();
const block = (intakeBlocks || []).find((item) =>
    [item?.programmeId, item?.programme, item?.program, item?.programId, item?.label, item?.name, item?.intakeName, item?.id, item?.uid]
    .map((v) => String(v || "").trim().toLowerCase()).includes(lowered));
const resolvedProgrammeId = String(block?.programmeId || block?.programme || block?.program || block?.programId || programmeId).trim();
const key = resolvedProgrammeId.toLowerCase();
if (seen.has(key)) return;
seen.add(key);
matches.push(block || { id: programmeId, uid: programmeId, label: programmeId, programmeId: resolvedProgrammeId });
});
return matches;
}

function collectStudentProgrammeValues(student) {
const values = [];
const add = (value) => {
if (Array.isArray(value)) { value.forEach(add); return; }
if (value && typeof value === "object") {
    [value.programmeId, value.programId, value.programme, value.program, value.programmeName, value.programName, value.name, value.id].forEach(add);
    return;
}
const safe = String(value || "").trim();
if (safe) values.push(safe);
};
[
student?.programmeId, student?.programId, student?.programme, student?.program,
student?.programmeName, student?.programName, student?.activeProgrammeId, student?.activeProgramId,
student?.activeProgramme, student?.activeProgram, student?.discipline, student?.selectedProgramme,
student?.selectedProgram, student?.course, student?.courseName, student?.courses,
student?.programmes, student?.programs, student?.enrolledProgrammes, student?.enrolledPrograms, student?.enrolledCourses,
].forEach(add);
return Array.from(new Set(values));
}

function resolveStudentProgrammeId(student, intakeBlocks = []) {
const direct = String(student?.programmeId || student?.programId || student?.programme || "").trim();
if (direct) return direct;
const intakeIds = [
...Object.entries(student?.intakeAssignment || {}).filter(([, enabled]) => Boolean(enabled)).map(([id]) => id),
...(Array.isArray(student?.selectedIntakeBlockIds) ? student.selectedIntakeBlockIds : []),
student?.selectedIntakeBlockId,
].map((id) => String(id || "").trim()).filter(Boolean);
for (const intakeId of intakeIds) {
const lowered = intakeId.toLowerCase();
const block = (intakeBlocks || []).find((item) =>
    [item?.id, item?.uid, item?.uniqueId, item?.intakeId, item?.docId, item?.label, item?.name, item?.intakeName]
    .map((v) => String(v || "").trim().toLowerCase()).includes(lowered));
const programmeId = String(block?.programmeId || block?.programme || block?.program || block?.programId || "").trim();
if (programmeId) return programmeId;
}
return "";
}

async function resolveProgrammeProgressMeta(programmeId) {
if (!programmeId) return { assessmentTotal: 0, unitIds: [] };
try {
const gsd = httpsCallable(getFunctions(), "getSystemDocuments");
const getProgramDetails = httpsCallable(getFunctions(), "getProgramDetails");
const programmeSources = [];
try {
    const psRes = await gsd({ company: COMPANY, collectionBrand: COLLECTION_BRAND, systemName: "programStructure", uniqueId: programmeId, auth: true });
    if (psRes?.data?.data) programmeSources.push(psRes.data.data);
} catch { /* try programme details below */ }
try {
    const result = await getProgramDetails({ programId: programmeId, company: COMPANY });
    if (result?.data?.success && result.data.data) programmeSources.push(result.data.data);
} catch { /* no programme details */ }
const programStructure = programmeSources.filter(Boolean)
    .sort((a, b) => collectAllProgrammeUnitIds(b, programmeId).length - collectAllProgrammeUnitIds(a, programmeId).length)[0] || null;
if (!programStructure?.modules?.length) return { assessmentTotal: 0, unitIds: [] };
const unitIds = collectAllProgrammeUnitIds(programStructure, programmeId);
const assessmentTotal = await countProgrammeAssessments(programStructure, programmeId);
return { assessmentTotal, unitIds };
} catch (err) {
console.warn("[SupervisorPortalDashboard] programme progress meta failed:", programmeId, err?.message);
return { assessmentTotal: 0, unitIds: [] };
}
}

async function resolveLearnerUnitProgress(programmeId, student, unitIds = []) {
const total = Array.isArray(unitIds) ? unitIds.length : 0;
if (!programmeId || total === 0) return { done: 0, total: 0, percent: 0 };
try {
const unitsMap = await fetchProgramLearnerProgressUnitsMap(programmeId, buildLearnerLookupIds(student), unitIds, "fetchLearnerIntakeStructurePreviewV2");
const summary = summarizeLearningProgressUnits(unitsMap, unitIds);
const safeTotal = summary.total || total;
const safeDone = Math.min(summary.done || 0, safeTotal);
return { done: safeDone, total: safeTotal, percent: safeTotal > 0 ? Math.round((safeDone / safeTotal) * 100) : 0 };
} catch (err) {
console.warn("[SupervisorPortalDashboard] learner unit progress failed:", student?.email, err?.message);
return { done: 0, total, percent: 0 };
}
}

function buildLearnerLookupIds(student) {
return Array.from(new Set([
student?.email, student?.uid, student?.firebaseUid, student?.userId,
student?.learnerId, student?.authUid, student?.id, student?.studentNumber,
].map((v) => String(v || "").trim()).filter(Boolean)));
}

function summarizeLearningProgressUnits(unitsMap, expectedUnitIds = []) {
const expected = new Set((expectedUnitIds || []).map((id) => String(id || "").trim()).filter(Boolean));
const rows = Object.entries(unitsMap || {}).filter(([unitId]) => !expected.size || expected.has(String(unitId || "").trim()));
const total = expected.size || rows.length;
const done = rows.filter(([, unit]) => isLearningUnitComplete(unit)).length;
return { total, done };
}

function isLearningUnitComplete(unit) {
const total = Number(unit?.totalAssessments ?? unit?.total ?? unit?.assessmentTotal ?? unit?.requiredAssessments ?? 0);
const completed = Number(unit?.completedAssessments ?? unit?.written ?? unit?.completed ?? unit?.assessmentsCompleted ?? 0);
const percent = Number(unit?.progressPercent ?? unit?.percent ?? unit?.progress ?? 0);
if (total > 0) return completed >= total;
return percent >= 100 || unit?.status === "completed" || unit?.completed === true;
}

async function countProgrammeAssessments(programStructure, programmeId) {
const unitNames = [];
for (const mod of programStructure?.modules || []) {
for (const compName of mod.components || []) {
    const comp = programStructure.components?.find((item) => item.name === compName);
    for (const unitName of comp?.units || []) {
    if (!unitNames.includes(unitName)) unitNames.push(unitName);
    }
}
}
const totals = await Promise.all(unitNames.map(async (unitName) => {
const unitId = buildUnitId(unitName, programmeId, COMPANY);
try {
    const res = await fetchAIContentBuilderData(programmeId, unitId, COMPANY, COLLECTION_BRAND);
    if (!res?.success || !res?.data?.unit?.outcomes) return 0;
    let count = 0;
    for (const outcome of res.data.unit.outcomes || []) {
    for (const section of outcome.outline?.sections || []) {
        const assessment = section.complexities?.assessment;
        if (assessment && (!Array.isArray(assessment) || assessment.length > 0)) count += 1;
    }
    }
    return count;
} catch { return 0; }
}));
return totals.reduce((sum, count) => sum + count, 0);
}

function formatRelativeTime(date) {
if (!date) return "Not updated yet";
const ms = date instanceof Date ? date.getTime() : new Date(date).getTime();
if (!Number.isFinite(ms)) return "Not updated yet";
const diffMs = Math.max(0, Date.now() - ms);
const diffMinutes = Math.floor(diffMs / 60000);
if (diffMinutes < 1) return "Updated just now";
if (diffMinutes < 60) return `Updated ${diffMinutes} min${diffMinutes === 1 ? "" : "s"} ago`;
const diffHours = Math.floor(diffMinutes / 60);
if (diffHours < 24) return `Updated ${diffHours} hour${diffHours === 1 ? "" : "s"} ago`;
const diffDays = Math.floor(diffHours / 24);
return `Updated ${diffDays} day${diffDays === 1 ? "" : "s"} ago`;
}

function pctFromEntry(e) {
const percent = e.assessment?.percent;
if (percent != null && Number.isFinite(Number(percent))) return Number(percent);
const maxScore = Number(e.assessment?.maxScore || 0);
return maxScore > 0 ? (100 * Number(e.assessment?.score || 0)) / maxScore : 0;
}
